import type { CallGridView, CampaignMetrics, CampaignSource, CampaignsReportResponse, ReportRangeView } from "@gioia365/contracts";
import { emptyReportMetrics, REPORT_KPI_GROUPS, reportKpiValue, type ReportDelta, type ReportKpiGroupId, type ReportKpiId, type ReportKpiUnit, type ReportMetrics, type ReportTarget, type ReportTargets } from "@gioia365/domain/report-kpis";
import type { Locale, MessageKey } from "@gioia365/i18n";
import { formatCount, formatEuroCents, formatNumericDate, formatNumericTime } from "../i18n/format";
import type { Translate } from "../i18n/locale";
import { dayCountOf } from "./report-search";

// Come il Report scrive numeri, frecce, obiettivi e periodi (piano M7, Task 3; mock-up `report.html`: `fmt`, `deltaBadge`,
// `targetBadge`, `rangeText`, `compareText`, `fmtDayLong`, `fmtDayShort`). Le percentuali hanno un decimale («25,0%»), i minuti si
// leggono in ore e minuti («3 h 20 min»), gli importi nella lingua della persona («4.800 €», «€4,800»); un numero senza denominatore è
// «—». Le date sono in numeri con il giorno della settimana in lettere (UX-5), l'ora «fino alle» nel fuso dell'azienda.

const percentFormatters = new Map<Locale, Intl.NumberFormat>();
const targetFormatters = new Map<Locale, Intl.NumberFormat>();

/** Una percentuale con un decimale, senza il segno: «25,0», «25.0». */
function percentNumber(locale: Locale, value: number): string {
  let formatter = percentFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { useGrouping: "always", minimumFractionDigits: 1, maximumFractionDigits: 1 });
    percentFormatters.set(locale, formatter);
  }
  return formatter.format(value);
}

/** Il numero di un obiettivo: intero se lo è («8», «50»), altrimenti con il suo decimale («18,5»). */
export function targetNumber(locale: Locale, value: number): string {
  let formatter = targetFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { useGrouping: "always", maximumFractionDigits: 1 });
    targetFormatters.set(locale, formatter);
  }
  return formatter.format(value);
}

/** I minuti come li scrive il mock-up (`fmtMin`): «45 min», «3 h 20 min». Le unità sono le stesse in italiano e in inglese. */
function minutesText(locale: Locale, minutes: number): string {
  const hours = Math.floor(minutes / 60);
  return hours > 0 ? `${formatCount(locale, hours)} h ${minutes % 60} min` : `${formatCount(locale, minutes)} min`;
}

/** Un numero del Report nella sua unità: «—» quando non c'è (un denominatore a zero). */
export function formatKpi(locale: Locale, unit: ReportKpiUnit, value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  switch (unit) {
    case "percent":
      return `${percentNumber(locale, value)}%`;
    case "minutes":
      return minutesText(locale, value);
    case "cents":
      return formatEuroCents(locale, value);
    default:
      return formatCount(locale, value);
  }
}

/** Il segno della freccia: «+» in su, «−» (il segno meno, non il trattino) in giù. */
const signOf = (change: number) => (change > 0 ? "+" : "−");

/**
 * Il testo della freccia (`deltaBadge` del mock-up): le percentuali in punti con al più un decimale («+1,2 punti», «+2 punti»), gli
 * altri numeri in percento intero («−8%»), «prima nessuna» se prima era zero, «stabile» se non cambia. Il decimale dei punti c'è
 * soltanto per le percentuali (`unit`); il separatore lo scrive il catalogo della lingua.
 */
export function deltaText(t: Translate, delta: ReportDelta, unit: ReportKpiUnit): string {
  if (delta.kind === "stable") return t("reports.delta.stable");
  if (delta.kind === "first") return t("reports.delta.first");
  const size = Math.abs(delta.change);
  if (delta.kind === "percent") return t("reports.delta.percent", { sign: signOf(delta.change), value: String(Math.round(size)) });
  const tenths = unit === "percent" ? Math.round(size * 10) : Math.round(size) * 10;
  const whole = Math.floor(tenths / 10);
  const rest = tenths % 10;
  return t("reports.delta.points", { sign: signOf(delta.change), value: rest === 0 ? String(whole) : `${whole}${t("reports.delta.decimal")}${rest}` });
}

/** La frase nascosta dopo la freccia, per lo screen reader: il numero di prima, scritto come quello di adesso. */
export function deltaHiddenText(t: Translate, locale: Locale, unit: ReportKpiUnit, previous: number | null): string {
  return t("reports.delta.hidden", { value: formatKpi(locale, unit, previous) });
}

/** Gli obiettivi con un numero solo che si leggono «almeno»: lo show up delle demo (SEZ-2); il close rate si scrive col solo numero. */
const AT_LEAST: ReadonlySet<ReportKpiId> = new Set<ReportKpiId>(["demoShow"]);

/** L'obiettivo di un numero, come nel mock-up: «obiettivo 8–10%», «obiettivo almeno 50%», «obiettivo 20%». */
export function targetText(t: Translate, locale: Locale, id: ReportKpiId, target: ReportTarget): string {
  const min = targetNumber(locale, target.min);
  if (target.max !== undefined) return t("reports.target.range", { min, max: targetNumber(locale, target.max) });
  return AT_LEAST.has(id) ? t("reports.target.atLeast", { min }) : t("reports.target.exact", { min });
}

const TARGET_WORDS: Readonly<Record<"below" | "inLine" | "above", MessageKey>> = {
  below: "reports.target.words.below",
  inLine: "reports.target.words.inLine",
  above: "reports.target.words.above",
};

/** La parola accanto all'obiettivo: «sotto», «in linea», «sopra». */
export function targetWordText(t: Translate, word: "below" | "inLine" | "above"): string {
  return t(TARGET_WORDS[word]);
}

type IsoWeekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** Il giorno della settimana di un giorno di calendario, da 1 (lunedì) a 7 (domenica). */
function isoWeekdayOf(day: string): IsoWeekday {
  const weekday = new Date(`${day}T12:00:00Z`).getUTCDay();
  return (weekday === 0 ? 7 : weekday) as IsoWeekday;
}

/** Il nome di un giorno della settimana ISO (lunedì 1 … domenica 7), intero («giovedì») o corto («gio»). */
export function weekdayText(t: Translate, weekday: number, form: "long" | "short"): string {
  const day = String(weekday) as `${IsoWeekday}`;
  return t(`reports.weekday.${form}.${day}`);
}

/** «giovedì 15/10/2026» (`fmtDayLong` del mock-up), con i nomi dei giorni del calendario. */
export function dayLongText(t: Translate, day: string): string {
  return t("reports.range.dayName", { weekday: weekdayText(t, isoWeekdayOf(day), "long"), date: formatNumericDate("UTC", day) });
}

/** «gio 15/10/2026» (`fmtDayShort` del mock-up), le righe di «Giorno per giorno». */
export function dayShortText(t: Translate, day: string): string {
  return t("reports.range.dayName", { weekday: weekdayText(t, isoWeekdayOf(day), "short"), date: formatNumericDate("UTC", day) });
}

/**
 * Il periodo (`rangeText` del mock-up), con la minuscola: «giovedì 15/10/2026, fino alle 16:20», «dal 16/09/2026 al 15/10/2026, oggi
 * fino alle 16:20», «mercoledì 14/10/2026». L'ora è quella in cui il periodo finisce, nel fuso dell'azienda.
 */
export function rangeText(t: Translate, timeZone: string, range: ReportRangeView): string {
  const time = range.endsAt === null ? null : formatNumericTime(timeZone, range.endsAt);
  if (range.from === range.to) {
    const day = dayLongText(t, range.from);
    return time === null ? day : t("reports.range.dayUntil", { day, time });
  }
  const from = formatNumericDate("UTC", range.from);
  const to = formatNumericDate("UTC", range.to);
  return time === null ? t("reports.range.days", { from, to }) : t("reports.range.daysUntil", { from, to, time });
}

/** La frase del confronto (`compareText` del mock-up): il periodo prima e, quando quello di adesso arriva a oggi, la stessa ora. */
export function compareText(t: Translate, timeZone: string, previous: ReportRangeView): string {
  const time = previous.endsAt === null ? null : formatNumericTime(timeZone, previous.endsAt);
  if (previous.from === previous.to) {
    const day = dayLongText(t, previous.from);
    return time === null ? t("reports.compare.day", { day }) : t("reports.compare.dayUntil", { day, time });
  }
  const values = { count: dayCountOf(previous.from, previous.to), from: formatNumericDate("UTC", previous.from), to: formatNumericDate("UTC", previous.to) };
  return time === null ? t("reports.compare.days", values) : t("reports.compare.daysUntil", { ...values, time });
}

/** Una frase che comincia con un pezzo scritto in minuscola («Dal 16/09/2026 …», «Giovedì 15/10/2026 …»). */
export function sentenceStart(locale: Locale, text: string): string {
  return text.slice(0, 1).toLocaleUpperCase(locale) + text.slice(1);
}

/** Le parole di ogni numero del foglio: il nome, quello corto delle colonne e, quando c'è, la formula detta a parole. */
export const KPI_TEXTS: Readonly<Record<ReportKpiId, { label: MessageKey; short: MessageKey; how?: MessageKey }>> = {
  calls: { label: "reports.kpis.calls.label", short: "reports.kpis.calls.short", how: "reports.kpis.calls.how" },
  answered: { label: "reports.kpis.answered.label", short: "reports.kpis.answered.short" },
  answerRate: { label: "reports.kpis.answerRate.label", short: "reports.kpis.answerRate.short", how: "reports.kpis.answerRate.how" },
  leads: { label: "reports.kpis.leads.label", short: "reports.kpis.leads.short", how: "reports.kpis.leads.how" },
  leadRate: { label: "reports.kpis.leadRate.label", short: "reports.kpis.leadRate.short", how: "reports.kpis.leadRate.how" },
  minutes: { label: "reports.kpis.minutes.label", short: "reports.kpis.minutes.short", how: "reports.kpis.minutes.how" },
  discoBooked: { label: "reports.kpis.discoBooked.label", short: "reports.kpis.discoBooked.short", how: "reports.kpis.discoBooked.how" },
  discoOnCalls: { label: "reports.kpis.discoOnCalls.label", short: "reports.kpis.discoOnCalls.short", how: "reports.kpis.discoOnCalls.how" },
  discoOnAnswered: { label: "reports.kpis.discoOnAnswered.label", short: "reports.kpis.discoOnAnswered.short", how: "reports.kpis.discoOnAnswered.how" },
  discoDue: { label: "reports.kpis.discoDue.label", short: "reports.kpis.discoDue.short", how: "reports.kpis.discoDue.how" },
  discoHeld: { label: "reports.kpis.discoHeld.label", short: "reports.kpis.discoHeld.short", how: "reports.kpis.discoHeld.how" },
  discoShow: { label: "reports.kpis.discoShow.label", short: "reports.kpis.discoShow.short", how: "reports.kpis.discoShow.how" },
  discoRescheduled: { label: "reports.kpis.discoRescheduled.label", short: "reports.kpis.discoRescheduled.short", how: "reports.kpis.discoRescheduled.how" },
  demoBooked: { label: "reports.kpis.demoBooked.label", short: "reports.kpis.demoBooked.short", how: "reports.kpis.demoBooked.how" },
  demoOnDisco: { label: "reports.kpis.demoOnDisco.label", short: "reports.kpis.demoOnDisco.short", how: "reports.kpis.demoOnDisco.how" },
  demoDue: { label: "reports.kpis.demoDue.label", short: "reports.kpis.demoDue.short", how: "reports.kpis.demoDue.how" },
  demoHeld: { label: "reports.kpis.demoHeld.label", short: "reports.kpis.demoHeld.short", how: "reports.kpis.demoHeld.how" },
  demoRescheduled: { label: "reports.kpis.demoRescheduled.label", short: "reports.kpis.demoRescheduled.short", how: "reports.kpis.demoRescheduled.how" },
  demoShow: { label: "reports.kpis.demoShow.label", short: "reports.kpis.demoShow.short", how: "reports.kpis.demoShow.how" },
  closings: { label: "reports.kpis.closings.label", short: "reports.kpis.closings.short", how: "reports.kpis.closings.how" },
  closeRate: { label: "reports.kpis.closeRate.label", short: "reports.kpis.closeRate.short", how: "reports.kpis.closeRate.how" },
  value: { label: "reports.kpis.value.label", short: "reports.kpis.value.short", how: "reports.kpis.value.how" },
  averageValue: { label: "reports.kpis.averageValue.label", short: "reports.kpis.averageValue.short", how: "reports.kpis.averageValue.how" },
};

/** Il titolo di un gruppo del foglio. */
export const KPI_GROUP_TITLES: Readonly<Record<ReportKpiGroupId, MessageKey>> = {
  calls: "reports.groups.calls",
  disco: "reports.groups.disco",
  demo: "reports.groups.demo",
  closings: "reports.groups.closings",
};

/** Il gruppo del foglio a cui appartiene un numero. */
export function kpiGroupOf(id: ReportKpiId): ReportKpiGroupId {
  const group = REPORT_KPI_GROUPS.find((candidate) => candidate.kpis.includes(id));
  if (!group) throw new RangeError(`report KPI without group: ${id}`);
  return group.id;
}

/** L'obiettivo di un numero, se ne ha uno (i sei di `ReportTargets`). */
export function targetOf(targets: ReportTargets, id: ReportKpiId): ReportTarget | undefined {
  return Object.hasOwn(targets, id) ? targets[id as keyof ReportTargets] : undefined;
}

/** Un periodo senza niente da contare (`isEmpty` del mock-up): nessuna chiamata, nessuna disco o demo, nessuna vendita. */
export function isEmptyReport(metrics: ReportMetrics): boolean {
  return metrics.calls === 0 && metrics.discoBooked === 0 && metrics.discoDue === 0 && metrics.demoDue === 0 && metrics.closings === 0;
}

const averageFormatters = new Map<string, Intl.NumberFormat>();

/** Una media al giorno con `decimals` decimali, nella lingua della persona: «41», «0,8». */
export function averageText(locale: Locale, value: number, decimals: 0 | 1): string {
  const key = `${locale}:${decimals}`;
  let formatter = averageFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { useGrouping: "always", minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    averageFormatters.set(key, formatter);
  }
  return formatter.format(value);
}

/** «1 chiamata», «12 chiamate» (`callsWord` del mock-up). */
export function callsText(t: Translate, locale: Locale, calls: number): string {
  return calls === 1 ? t("reports.table.callsOne") : t("reports.table.callsMany", { count: formatCount(locale, calls) });
}

// Le ore della griglia (Task 4; `heatSection` e l'andamento di un giorno solo del mock-up): la colonna dell'apertura con i suoi minuti
// («8:30»), le altre con l'ora piena; le frasi di una cella dicono il giorno, le ore, le chiamate o perché si è chiusi. Una cella chiusa
// del venerdì dopo la mattina, con «il venerdì solo la mattina», lo dice con le parole del mock-up; le altre (prima dell'apertura, dopo
// la chiusura, un giorno chiuso) sono «chiuso».

type GridRow = CallGridView["rows"][number];

/** L'etichetta di una colonna: «8:30» per quella dell'apertura alle 8:30, «9» per le altre. */
export function hourLabel(grid: Pick<CallGridView, "hours" | "firstMinute">, hour: number): string {
  const minute = hour === grid.hours[0] ? grid.firstMinute : 0;
  return minute === 0 ? String(hour) : `${hour}:${String(minute).padStart(2, "0")}`;
}

/** «dalle 8:30 alle 9». */
export function hourSpanText(t: Translate, grid: Pick<CallGridView, "hours" | "firstMinute">, hour: number): string {
  return t("reports.hours.span", { from: hourLabel(grid, hour), to: String(hour + 1) });
}

/** Che cosa è una cella per chi legge: lavoro, pausa, il venerdì pomeriggio della sequenza o chiuso. */
export type GridCellKind = "work" | "pause" | "friday" | "closed";

/** Il tipo della cella `index` di una riga. */
export function gridCellKindOf(grid: Pick<CallGridView, "fridayMorningOnly">, row: GridRow, index: number): GridCellKind {
  const cell = row.cells[index];
  if (!cell) throw new RangeError(`No cell ${index}`);
  if (cell.state !== "closed") return cell.state;
  const afterMorning = row.cells.slice(0, index).some((earlier) => earlier.state === "work");
  return grid.fridayMorningOnly && row.weekday === 5 && afterMorning ? "friday" : "closed";
}

const KIND_WORDS: Readonly<Record<Exclude<GridCellKind, "work">, MessageKey>> = {
  pause: "reports.hours.pause",
  friday: "reports.hours.friday",
  closed: "reports.hours.closed",
};

/**
 * Che cosa dice una cella: «12 chiamate, 4 con risposta» (con `withRate` anche «(33,3%)», come la mappa), oppure la pausa o il
 * chiuso, con le chiamate dopo se qualcuno ha chiamato lo stesso.
 */
export function gridCellWhatText(t: Translate, locale: Locale, kind: GridCellKind, calls: number, answered: number, withRate: boolean): string {
  let numbers = t("reports.hours.calls", { calls: callsText(t, locale, calls), answered: formatCount(locale, answered) });
  if (withRate && calls > 0) {
    numbers += t("reports.hours.rate", { rate: formatKpi(locale, "percent", reportKpiValue("answerRate", { ...emptyReportMetrics(), calls, answered })) });
  }
  if (kind === "work") return numbers;
  const state = t(KIND_WORDS[kind]);
  return calls > 0 ? t("reports.hours.withCalls", { state, calls: numbers }) : state;
}

// «Campagne» (Task 5; `renderCampagne` del mock-up): la campagna con le parole della colonna «Provenienza» dell'elenco Lead, i numeri di
// una campagna come conteggi del Report (le formule e le frecce sono quelle del dominio: il valore medio è `averageValue`), quante
// vendite, e quando la scheda non ha niente da dire.

/** La campagna in parole: «Moduli di Meta · Masterclass Colore», «Close», «A mano»; senza provenienza, «Senza provenienza». */
export function campaignText(t: Translate, source: CampaignSource | null, campaign: string | null): string {
  if (source === null) return t("reports.campaigns.noSource");
  const name = t(`leadList.source.${source}`);
  return campaign ? t("leadList.source.withCampaign", { source: name, campaign }) : name;
}

/**
 * I numeri di una campagna come conteggi del Report, per usare le stesse formule e le stesse frecce di «Commerciali» (D2): i Lead nuovi
 * sono un conteggio come i «Lead diversi», le disco come le «Disco prenotate», le demo fatte come le «Demo effettuate» (per il close
 * rate), vendite e valore come chiusure e valore delle chiusure.
 */
export function campaignReportMetrics(metrics: CampaignMetrics): ReportMetrics {
  return { ...emptyReportMetrics(), leads: metrics.fresh, discoBooked: metrics.disco, demoHeld: metrics.demoHeld, closings: metrics.closings, valueCents: metrics.valueCents };
}

/** «1 vendita», «3 vendite». */
export function salesCountText(t: Translate, locale: Locale, count: number): string {
  return count === 1 ? t("reports.campaigns.salesOne") : t("reports.campaigns.salesMany", { count: formatCount(locale, count) });
}

/** «Campagne» senza niente da contare (il mock-up): nessun Lead entrato, nessuna disco, nessuna demo fatta, nessuna vendita, nessuna vendita elencata. */
export function isEmptyCampaigns(report: Pick<CampaignsReportResponse, "totals" | "sales">): boolean {
  const { fresh, back, disco, demoHeld } = report.totals.current;
  return fresh === 0 && back === 0 && disco === 0 && demoHeld === 0 && report.totals.current.closings === 0 && report.sales.length === 0;
}
